import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { 
  Home, Car, Shield, Wrench, Users, Clock, Phone, CheckCircle, 
  ArrowRight, Building, Key, Sparkles, Wallet, HeadphonesIcon
} from 'lucide-react';

const ServicesPage = () => {
  const { language } = useLanguage();

  const propertyServices = [
    {
      icon: Wallet,
      title: language === 'fr' ? 'Gestion des loyers' : 'Rent Management',
      description: language === 'fr' 
        ? 'Collecte des loyers, relances, gestion des impayés et versement régulier sur votre compte.'
        : 'Rent collection, reminders, unpaid management and regular payment to your account.'
    },
    {
      icon: Wrench,
      title: language === 'fr' ? 'Entretien & Maintenance' : 'Maintenance',
      description: language === 'fr'
        ? 'Interventions rapides pour réparations, nettoyage régulier et maintenance préventive de votre bien.'
        : 'Quick repairs, regular cleaning and preventive maintenance of your property.'
    },
    {
      icon: Shield,
      title: language === 'fr' ? 'Sécurité 24/7' : 'Security 24/7',
      description: language === 'fr'
        ? 'Surveillance, gestion des accès et intervention en cas d\'urgence pour protéger votre investissement.'
        : 'Surveillance, access management and emergency intervention to protect your investment.'
    },
    {
      icon: Users,
      title: language === 'fr' ? 'Gestion des locataires' : 'Tenant Management',
      description: language === 'fr'
        ? 'Sélection rigoureuse des locataires, états des lieux, gestion des entrées et sorties.'
        : 'Rigorous tenant selection, inventory, management of check-ins and check-outs.'
    },
    {
      icon: Building,
      title: language === 'fr' ? 'Valorisation du bien' : 'Property Enhancement',
      description: language === 'fr'
        ? 'Conseils d\'aménagement, photos professionnelles et optimisation de votre annonce.'
        : 'Furnishing advice, professional photos and listing optimization.'
    },
    {
      icon: HeadphonesIcon,
      title: language === 'fr' ? 'Support dédié' : 'Dedicated Support',
      description: language === 'fr'
        ? 'Un interlocuteur unique pour vous et vos locataires, disponible 6j/7.'
        : 'A single point of contact for you and your tenants, available 6 days a week.'
    }
  ];

  const transportFeatures = [
    {
      icon: Clock,
      title: language === 'fr' ? 'Disponibilité flexible' : 'Flexible Availability',
      description: language === 'fr'
        ? 'Réservation à l\'avance ou en dernière minute, nous nous adaptons à vos besoins.'
        : 'Advance or last-minute booking, we adapt to your needs.'
    },
    {
      icon: Shield,
      title: language === 'fr' ? 'Véhicules assurés' : 'Insured Vehicles',
      description: language === 'fr'
        ? 'Tous nos véhicules sont assurés et régulièrement entretenus pour votre sécurité.'
        : 'All our vehicles are insured and regularly maintained for your safety.'
    },
    {
      icon: Users,
      title: language === 'fr' ? 'Chauffeurs professionnels' : 'Professional Drivers',
      description: language === 'fr'
        ? 'Des chauffeurs expérimentés, ponctuels et connaissant parfaitement la région.'
        : 'Experienced, punctual drivers who know the region perfectly.'
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative py-20 md:py-32 px-4 md:px-8 bg-gradient-to-br from-primary/5 via-background to-accent/5">
        <div className="max-w-6xl mx-auto text-center">
          <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-primary/10 flex items-center justify-center">
            <Sparkles className="h-10 w-10 text-primary" />
          </div>
          <h1 className="font-heading text-3xl md:text-5xl font-bold mb-6" data-testid="services-title">
            {language === 'fr' ? 'Nos Services Experts' : 'Our Expert Services'}
          </h1>
          <p className="text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto mb-8">
            {language === 'fr'
              ? 'Plus qu\'une plateforme de logements, Roomia.net vous accompagne avec des services professionnels de gestion immobilière et de transport au Cameroun.'
              : 'More than a housing platform, Roomia.net supports you with professional property management and transport services in Cameroon.'}
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a href="https://wa.me/237682329126?text=Bonjour, je souhaite en savoir plus sur vos services" target="_blank" rel="noopener noreferrer">
              <Button size="lg" className="rounded-full gap-2" data-testid="contact-services-btn">
                <Phone className="h-5 w-5" />
                {language === 'fr' ? 'Nous contacter' : 'Contact us'}
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Property Management */}
      <section className="py-16 md:py-24 px-4 md:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-xl bg-primary flex items-center justify-center">
                  <Key className="h-6 w-6 text-primary-foreground" />
                </div>
                <span className="text-sm font-medium text-primary uppercase tracking-wider">
                  {language === 'fr' ? 'Service Premium' : 'Premium Service'}
                </span>
              </div>
              <h2 className="font-heading text-2xl md:text-4xl font-bold mb-4">
                {language === 'fr' ? 'Gestion Locative Complète' : 'Complete Property Management'}
              </h2>
              <p className="text-muted-foreground mb-6">
                {language === 'fr'
                  ? 'Vous êtes propriétaire mais n\'avez pas le temps de gérer votre bien ? Confiez-nous la gestion complète de votre logement meublé. Nous nous occupons de tout, de A à Z.'
                  : 'You own a property but don\'t have time to manage it? Entrust us with the complete management of your furnished accommodation. We take care of everything, from A to Z.'}
              </p>
              <ul className="space-y-3 mb-8">
                {[
                  language === 'fr' ? 'Rentabilité maximisée' : 'Maximized profitability',
                  language === 'fr' ? 'Tranquillité d\'esprit totale' : 'Total peace of mind',
                  language === 'fr' ? 'Rapports mensuels détaillés' : 'Detailed monthly reports',
                  language === 'fr' ? 'Aucun souci de gestion' : 'No management worries'
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-3">
                    <CheckCircle className="h-5 w-5 text-primary shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <a href="https://wa.me/237682329126?text=Bonjour, je souhaite confier la gestion de mon bien à Roomia.net" target="_blank" rel="noopener noreferrer">
                <Button className="rounded-full gap-2">
                  {language === 'fr' ? 'Demander un devis' : 'Request a quote'}
                  <ArrowRight className="h-4 w-4" />
                </Button>
              </a>
            </div>
            <div className="order-2 lg:order-1">
              <img
                src="https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=600"
                alt="Property Management"
                className="rounded-2xl shadow-2xl"
              />
            </div>
          </div>

          {/* Services Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {propertyServices.map((service, index) => {
              const Icon = service.icon;
              return (
                <Card key={index} className="hover:shadow-lg transition-shadow">
                  <CardContent className="p-6">
                    <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
                      <Icon className="h-6 w-6 text-primary" />
                    </div>
                    <h3 className="font-heading font-semibold text-lg mb-2">{service.title}</h3>
                    <p className="text-muted-foreground text-sm">{service.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Transport Service */}
      <section className="py-16 md:py-24 px-4 md:px-8 bg-gradient-to-br from-accent/5 via-background to-primary/5">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1 relative">
              <img
                src="/taxi-driver.png"
                alt="Professional Driver"
                className="rounded-2xl shadow-2xl"
              />
              <div className="absolute -bottom-4 -right-4 bg-accent text-accent-foreground px-6 py-3 rounded-xl shadow-lg font-semibold">
                {language === 'fr' ? '🚗 Service Premium' : '🚗 Premium Service'}
              </div>
            </div>
            <div className="order-1 lg:order-2">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-xl bg-accent flex items-center justify-center">
                  <Car className="h-6 w-6 text-accent-foreground" />
                </div>
                <span className="text-sm font-medium text-accent uppercase tracking-wider">
                  {language === 'fr' ? 'Nouveau Service' : 'New Service'}
                </span>
              </div>
              <h2 className="font-heading text-2xl md:text-4xl font-bold mb-4">
                {language === 'fr' ? 'Service de Transport' : 'Transport Service'}
              </h2>
              <p className="text-muted-foreground mb-6">
                {language === 'fr'
                  ? 'Besoin de vous déplacer ? Roomia.net met à votre disposition des véhicules avec chauffeur pour tous vos trajets au Cameroun. Aéroport, visites de logements, déménagements...'
                  : 'Need to get around? Roomia.net provides vehicles with drivers for all your trips in Cameroon. Airport transfers, property visits, moving...'}
              </p>
              
              <div className="space-y-4 mb-8">
                {transportFeatures.map((feature, index) => {
                  const Icon = feature.icon;
                  return (
                    <div key={index} className="flex gap-4">
                      <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center shrink-0">
                        <Icon className="h-5 w-5 text-accent" />
                      </div>
                      <div>
                        <h4 className="font-semibold">{feature.title}</h4>
                        <p className="text-sm text-muted-foreground">{feature.description}</p>
                      </div>
                    </div>
                  );
                })}
              </div>

              <a href="https://wa.me/237682329126?text=Bonjour, je souhaite réserver un véhicule avec chauffeur" target="_blank" rel="noopener noreferrer">
                <Button variant="outline" className="rounded-full gap-2">
                  {language === 'fr' ? 'Réserver un transport' : 'Book transport'}
                  <ArrowRight className="h-4 w-4" />
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-24 px-4 md:px-8 bg-primary text-primary-foreground">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-heading text-2xl md:text-4xl font-bold mb-6">
            {language === 'fr' 
              ? 'Prêt à simplifier votre gestion immobilière ?' 
              : 'Ready to simplify your property management?'}
          </h2>
          <p className="text-lg opacity-90 mb-8 max-w-2xl mx-auto">
            {language === 'fr'
              ? 'Contactez-nous dès aujourd\'hui pour discuter de vos besoins et obtenir un devis personnalisé.'
              : 'Contact us today to discuss your needs and get a personalized quote.'}
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a href="https://wa.me/237682329126" target="_blank" rel="noopener noreferrer">
              <Button size="lg" variant="secondary" className="rounded-full gap-2">
                <Phone className="h-5 w-5" />
                +237 682 329 126
              </Button>
            </a>
            <Link to="/contact">
              <Button size="lg" variant="outline" className="rounded-full border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground/10">
                {language === 'fr' ? 'Formulaire de contact' : 'Contact form'}
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;
